import { useEffect, useReducer, useCallback } from 'react'
import { ethers } from 'ethers'
import Web3Modal from 'web3modal'
import WalletConnectProvider from '@walletconnect/web3-provider'

import {
  web3InitialState,
  web3Reducer,
} from '../reducers'

import customToast from './customToast'
import { supportedChainIds, defaultChainID } from '../constants'
import { switchNetwork } from './Web3CustomHooks'

const providerOptions = {
  walletconnect: {
    package: WalletConnectProvider, // required
    options: {
      infuraId: process.env.NEXT_PUBLIC_INFURA_ID,
    },
  },
}

let web3Modal;
if (typeof window !== 'undefined') {
  web3Modal = new Web3Modal({
    network: 'mainnet', // optional
    cacheProvider: true,
    providerOptions, // required
  })
}

export const useWeb3 = () => {
  const [state, dispatch] = useReducer(web3Reducer, web3InitialState)
  const { provider, web3Provider, address, network, error, error_message } = state

  const connect = useCallback(async () => {
    if (web3Modal) {
      try {
        if (!window.ethereum) {
          customToast.success('Install Metamask');
          window.open('https://chrome.google.com/webstore/detail/metamask/nkbihfbeogaeaoehlefnkodbefgpgknn?hl=en-US')
          return
        }
        const provider = await web3Modal.connect()
        const web3Provider = new ethers.providers.Web3Provider(provider)
        const signer = web3Provider.getSigner()
        const address = await signer.getAddress()
        const network = await web3Provider.getNetwork()

        if (!supportedChainIds.includes(network.chainId)) {
          // Don't immediately show error - let user choose to switch
          dispatch({
            type: 'SET_WEB3_PROVIDER_NETWORK_NOT_SUPPORTED',
            provider,
            web3Provider,
            address,
            network,
            error: "Network",
            error_message: "This network is not supported",
          })
        } else {
          customToast.success('Connected to Metamask')
          dispatch({
            type: 'SET_WEB3_PROVIDER',
            provider,
            web3Provider,
            address,
            network,
          })
        }
      } catch (e) {
        console.log('connect error', e)
      }
    } else {
      console.error('No Web3Modal')
    }
  }, [])

  const switchToSupportedNetwork = useCallback(async (targetChainId = defaultChainID) => {
    try {
      customToast.info('Switching network...')
      await switchNetwork(targetChainId)
      customToast.success('Network switched successfully!')
      
      // Reconnect after network switch
      setTimeout(() => {
        connect()
      }, 1000)
    } catch (error) {
      console.error('Network switch error:', error)
      customToast.error('Failed to switch network. Please switch manually in your wallet.')
    }
  }, [connect])

  const disconnect = useCallback(async () => {
    if (web3Modal) {
      web3Modal.clearCachedProvider()
      if (provider?.disconnect && typeof provider.disconnect === 'function') {
        await provider.disconnect()
      }
      customToast.error('Disconnected from Web3')
      dispatch({
        type: 'RESET_WEB3_PROVIDER',
      })
    } else {
      console.error('No Web3Modal')
    }
  }, [provider])

  // Auto connect to the cached provider
  useEffect(() => {
    if (web3Modal && web3Modal.cachedProvider) {
      connect()
    }
  }, [connect])

  // EIP-1193 events
  useEffect(() => {
    if (provider?.on) {
      const handleAccountsChanged = (accounts) => {
        customToast.info('Changed Web3 Account')
        dispatch({
          type: 'SET_ADDRESS',
          address: accounts[0],
        })
      }

      // https://docs.ethers.io/v5/concepts/best-practices/#best-practices--network-changes
      const handleChainChanged = (_hexChainId) => {
        if (typeof window !== 'undefined') {
          console.log('switched to chain...', _hexChainId)
          customToast.info('Web3 Network Changed')
          window.location.reload()
        } else {
          console.log('window is undefined')
        }
      }

      const handleDisconnect = (error) => {
        // eslint-disable-next-line no-console
        console.log('disconnect', error)
        disconnect()
      }

      provider.on('accountsChanged', handleAccountsChanged)
      provider.on('chainChanged', handleChainChanged)
      provider.on('disconnect', handleDisconnect)

      // Subscription Cleanup
      return () => {
        if (provider.removeListener) {
          provider.removeListener('accountsChanged', handleAccountsChanged)
          provider.removeListener('chainChanged', handleChainChanged)
          provider.removeListener('disconnect', handleDisconnect)
        }
      }
    }
  }, [provider, disconnect])

  return {
    provider,
    web3Provider,
    address,
    network,
    connect,
    disconnect,
    switchToSupportedNetwork,
    error,
    error_message
  }
}
